import { useCallback, useEffect, useMemo, useState } from 'react';
import { bookingApi, BookingApiError, setUnauthorizedHandler } from './lib/api';
import { errorKeyFor, useT } from './lib/i18n';
import { useDraft, useSession, useUi } from './lib/store';
import { resolveTenantRoute, stripQuery } from './lib/tenant';
import { AuthSheet } from './features/auth/AuthSheet';
import { BookedScreen, BookingFlow } from './features/booking/BookingFlow';
import { PaymentReturn } from './features/booking/PaymentReturn';
import { MyBookings } from './features/bookings/MyBookings';
import { ErrorNote, PortalShell, Skeleton } from './features/shell/PortalShell';
import type { Booking, Site } from './lib/types';

/**
 * Portal root.
 *
 * Routing is by URL shape rather than a router: the portal has four states
 * (booking, my bookings, post-booking, payment return) and a single tenant per
 * page load, so react-router would add a dependency for nothing.
 */

type View = 'book' | 'bookings' | 'booked' | 'payment';

export function App(): JSX.Element {
  const route = useMemo(() => resolveTenantRoute(), []);
  const { t } = useT();
  const site = useUi((s) => s.site);
  const setSite = useUi((s) => s.setSite);
  const setLang = useUi((s) => s.setLang);
  const hydrate = useSession((s) => s.hydrate);
  const signOut = useSession((s) => s.signOut);
  const draft = useDraft();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [view, setView] = useState<View>(route.chargeId || route.reference ? 'payment' : 'book');
  const [booked, setBooked] = useState<Booking | null>(null);
  const [chargeId, setChargeId] = useState<string | null>(route.chargeId);
  // Our own reference for the deposit charge (`pos_ref`), which every gateway's
  // return URL carries — the charge is found by it when the provider dropped
  // its own id from the query string.
  const [chargeRef, setChargeRef] = useState<string | null>(route.reference);

  const slug = route.slug;

  // A 401 anywhere means the stored token is dead; drop it rather than looping.
  useEffect(() => {
    if (!slug) return;
    setUnauthorizedHandler(() => signOut(slug));
  }, [slug, signOut]);

  const load = useCallback(async () => {
    if (!slug) {
      setError(t('err_no_tenant'));
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const data = await bookingApi.site(slug);
      setSite(data);
      applyTheme(data);
      // Honour a stored preference; otherwise follow the tenant's default.
      if (!localStorage.getItem('booking:lang')) {
        setLang(data.default_language === 'ar' ? 'ar' : 'en');
      }
      document.title = data.name;
      await hydrate(slug);
    } catch (err) {
      setError(err instanceof BookingApiError ? t(errorKeyFor(err.code)) : t('err_generic'));
    } finally {
      setLoading(false);
    }
  }, [slug, setSite, setLang, hydrate, t]);

  useEffect(() => {
    void load();
  }, [load]);

  // A redirect can lose its query string on some bank flows; the charge id and
  // our reference were stashed before leaving, so recover them rather than
  // stranding the customer.
  useEffect(() => {
    if (!slug || view !== 'payment') return;
    if (!chargeId) {
      const stored = sessionStorage.getItem(`booking:${slug}:charge`);
      if (stored) setChargeId(stored);
    }
    if (!chargeRef) {
      const storedRef = sessionStorage.getItem(`booking:${slug}:charge-ref`);
      if (storedRef) setChargeRef(storedRef);
    }
  }, [slug, chargeId, chargeRef, view]);

  useEffect(() => {
    if (route.chargeId || route.reference) stripQuery();
  }, [route.chargeId, route.reference]);

  const { lang, dir } = useT();
  useEffect(() => {
    document.documentElement.lang = lang;
    document.documentElement.dir = dir;
  }, [lang, dir]);

  if (loading) {
    return (
      <div className="bk-gutter mx-auto w-full max-w-3xl py-10">
        <div className="bk-skeleton mb-6 h-12" />
        <Skeleton rows={4} />
      </div>
    );
  }

  if (error || !site || !slug) {
    return (
      <div className="bk-gutter mx-auto w-full max-w-md py-16">
        <ErrorNote message={error ?? t('err_generic')} onRetry={slug ? () => void load() : undefined} />
      </div>
    );
  }

  return (
    <>
      <PortalShell
        site={site}
        slug={slug}
        view={view === 'bookings' ? 'bookings' : 'book'}
        onNavigate={(next) => {
          setBooked(null);
          setView(next);
        }}
      >
        {view === 'payment' && (chargeId || chargeRef) ? (
          <PaymentReturn
            slug={slug}
            chargeId={chargeId}
            reference={chargeRef}
            onDone={(booking) => {
              draft.reset();
              setBooked(booking);
              setView('booked');
            }}
            onFailed={() => {
              setChargeId(null);
              setChargeRef(null);
              setView('book');
            }}
          />
        ) : view === 'booked' && booked ? (
          <BookedScreen
            booking={booked}
            onBookAnother={() => {
              draft.reset();
              setBooked(null);
              setView('book');
            }}
            onViewBookings={() => {
              setBooked(null);
              setView('bookings');
            }}
          />
        ) : view === 'bookings' ? (
          <MyBookings slug={slug} site={site} />
        ) : (
          <BookingFlow
            site={site}
            slug={slug}
            route={route}
            onBooked={(booking) => {
              setBooked(booking);
              setView('booked');
            }}
          />
        )}
      </PortalShell>
      <AuthSheet slug={slug} />
    </>
  );
}

/**
 * Push the tenant's accent colour into CSS custom properties, and pick a
 * readable foreground for it so a pale brand colour does not produce white
 * text on white buttons.
 */
function applyTheme(site: Site): void {
  const root = document.documentElement;
  const accent = /^#[0-9a-f]{6}$/i.test(site.accent_color) ? site.accent_color : '#7c3aed';
  root.style.setProperty('--accent', accent);
  root.style.setProperty('--accent-soft', hexToRgba(accent, 0.1));
  root.style.setProperty('--ring', hexToRgba(accent, 0.35));
  root.style.setProperty('--accent-contrast', contrastOn(accent));
  document.querySelector('meta[name="theme-color"]')?.setAttribute('content', accent);
}

function hexToRgba(hex: string, alpha: number): string {
  const [r, g, b] = channels(hex);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/** WCAG relative luminance, used to choose black or white text on the accent. */
function contrastOn(hex: string): string {
  const [r, g, b] = channels(hex).map((c) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  }) as [number, number, number];
  const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  return luminance > 0.45 ? '#18181b' : '#ffffff';
}

function channels(hex: string): [number, number, number] {
  return [
    parseInt(hex.slice(1, 3), 16),
    parseInt(hex.slice(3, 5), 16),
    parseInt(hex.slice(5, 7), 16),
  ];
}
